/**
 * Real-time Sharing Service
 * 
 * Handles real-time collaboration features for sharing system including:
 * - Live sharing updates via WebSocket
 * - Conflict resolution for simultaneous edits
 * - Notification system for sharing activities
 * - User presence tracking
 */

class RealtimeSharingService {
  constructor() {
    this.eventListeners = new Map()
    this.activeUsers = new Map()
    this.conflictQueue = []
    this.isConnected = false
    this.websocketService = null
    
    // Conflict resolution strategies
    this.conflictStrategies = {
      'last_write_wins': this.resolveLastWriteWins.bind(this),
      'user_choice': this.resolveUserChoice.bind(this),
      'merge': this.resolveMerge.bind(this)
    }
  }

  /**
   * Initialize real-time sharing with WebSocket service
   */
  initialize(websocketService) {
    this.websocketService = websocketService
    this.setupEventHandlers()
  }

  /**
   * Setup WebSocket event handlers for sharing events
   */
  setupEventHandlers() {
    if (!this.websocketService) return

    // Listen for sharing-related WebSocket events
    window.addEventListener('websocketEvent', this.handleWebSocketEvent.bind(this))
    
    // Setup specific sharing event handlers
    this.websocketService.on('sharing_update', this.handleSharingUpdate.bind(this))
    this.websocketService.on('invitation_update', this.handleInvitationUpdate.bind(this))
    this.websocketService.on('permission_update', this.handlePermissionUpdate.bind(this))
    this.websocketService.on('conflict', this.handleConflict.bind(this))
    this.websocketService.on('user_activity', this.handleUserActivity.bind(this))
    this.websocketService.on('share_notification', this.handleShareNotification.bind(this))
  }

  /**
   * Subscribe to sharing events for a specific child
   */
  subscribeToChild(childId) {
    if (this.websocketService && this.websocketService.isConnected) {
      this.websocketService.subscribeToChild(childId)
    }
  }

  /**
   * Unsubscribe from sharing events for a specific child
   */
  unsubscribeFromChild(childId) {
    if (this.websocketService && this.websocketService.isConnected) {
      this.websocketService.unsubscribeFromChild(childId)
    }
  }

  /**
   * Subscribe to specific sharing events
   */
  subscribe(eventType, callback) {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set())
    }
    this.eventListeners.get(eventType).add(callback)
    
    return () => {
      const listeners = this.eventListeners.get(eventType)
      if (listeners) {
        listeners.delete(callback)
      }
    }
  }

  /**
   * Emit events to subscribers
   */
  emit(eventType, data) {
    const listeners = this.eventListeners.get(eventType)
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in event listener for ${eventType}:`, error)
        }
      })
    }
  }

  /**
   * Handle WebSocket events
   */
  handleWebSocketEvent(event) {
    const { type, data } = event.detail
    
    switch (type) {
      case 'sharing_update':
        this.handleSharingUpdate(data)
        break
      case 'invitation_update':
        this.handleInvitationUpdate(data)
        break
      case 'permission_update':
        this.handlePermissionUpdate(data)
        break
      case 'conflict':
        this.handleConflict(data)
        break
      case 'user_activity':
        this.handleUserActivity(data)
        break
      case 'share_notification':
        this.handleShareNotification(data)
        break
    }
  }

  /**
   * Handle sharing updates (invitations, acceptances, etc.)
   */
  handleSharingUpdate(data) {
    console.log('Sharing update received:', data)
    
    switch (data.event_type) {
      case 'invitation_sent':
        this.emit('invitation_sent', data)
        this.showNotification('Invitation sent successfully', 'success')
        break
        
      case 'invitation_accepted':
        this.emit('invitation_accepted', data)
        this.showNotification(
          `${data.accepted_by.name} accepted your invitation to access ${data.child_name}`,
          'success'
        )
        break
        
      case 'invitation_declined':
        this.emit('invitation_declined', data)
        this.showNotification(
          `${data.declined_email} declined your invitation to access ${data.child_name}`,
          'info'
        )
        break
        
      case 'access_revoked':
        this.emit('access_revoked', data)
        if (data.revoked_user.email === this.getCurrentUserEmail()) {
          this.showNotification(
            `Your access to ${data.child_name} has been revoked`,
            'warning'
          )
          // Redirect away from child if currently viewing
          this.handleAccessRevoked(data.child_id)
        } else {
          this.showNotification(
            `Access revoked for ${data.revoked_user.name}`,
            'info'
          )
        }
        break
    }
  }

  /**
   * Handle invitation updates
   */
  handleInvitationUpdate(data) {
    console.log('Invitation update received:', data)
    this.emit('invitation_received', data)
    
    if (data.event_type === 'invitation_sent') {
      this.showNotification(
        `You've been invited to access ${data.child_name}`,
        'info',
        {
          action: 'View Invitation',
          onClick: () => this.navigateToInvitation(data.share_id)
        }
      )
    }
  }

  /**
   * Handle permission updates
   */
  handlePermissionUpdate(data) {
    console.log('Permission update received:', data)
    this.emit('permission_updated', data)
    
    if (data.affected_user.email === this.getCurrentUserEmail()) {
      this.showNotification(
        `Your access level for ${data.child_name} has been changed to ${data.new_permission}`,
        'info'
      )
    } else {
      this.showNotification(
        `Permission updated for ${data.affected_user.name}`,
        'info'
      )
    }
  }

  /**
   * Handle conflict notifications
   */
  handleConflict(data) {
    console.log('Conflict detected:', data)
    this.conflictQueue.push(data)
    this.emit('conflict_detected', data)
    
    // Show conflict resolution UI
    this.showConflictResolution(data)
  }

  /**
   * Handle user activity updates
   */
  handleUserActivity(data) {
    console.log('User activity:', data)
    
    // Update active users list
    this.updateActiveUser(data.child_id, data.user, data.activity_type)
    
    this.emit('user_activity', data)
    
    // Show subtle activity indicator
    if (data.activity_type === 'editing') {
      this.showActivityIndicator(data.user, data.details)
    }
  }

  /**
   * Handle share notifications
   */
  handleShareNotification(data) {
    console.log('Share notification received:', data)
    this.emit('notification_received', data)
    
    // Show notification based on type
    const notificationTypes = {
      'invitation_sent': 'info',
      'invitation_accepted': 'success',
      'invitation_declined': 'info',
      'permission_changed': 'info',
      'access_revoked': 'warning',
      'data_shared': 'info'
    }
    
    const type = notificationTypes[data.notification_type] || 'info'
    this.showNotification(data.title, type, {
      message: data.message,
      persistent: data.notification_type === 'access_revoked'
    })
  }

  /**
   * Show conflict resolution UI
   */
  showConflictResolution(conflictData) {
    const modal = document.createElement('div')
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'

    const card = document.createElement('div')
    card.className = 'bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6'

    const header = document.createElement('div')
    header.className = 'flex items-center mb-4'

    const svgNamespace = 'http://www.w3.org/2000/svg'
    const icon = document.createElementNS(svgNamespace, 'svg')
    icon.setAttribute('class', 'w-6 h-6 text-yellow-500 mr-2')
    icon.setAttribute('fill', 'none')
    icon.setAttribute('stroke', 'currentColor')
    icon.setAttribute('viewBox', '0 0 24 24')
    const iconPath = document.createElementNS(svgNamespace, 'path')
    iconPath.setAttribute('stroke-linecap', 'round')
    iconPath.setAttribute('stroke-linejoin', 'round')
    iconPath.setAttribute('stroke-width', '2')
    iconPath.setAttribute(
      'd',
      'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z'
    )
    icon.appendChild(iconPath)

    const title = document.createElement('h3')
    title.className = 'text-lg font-semibold text-gray-900'
    title.textContent = 'Editing Conflict Detected'

    header.appendChild(icon)
    header.appendChild(title)

    const description = document.createElement('p')
    description.className = 'text-gray-600 mb-4'
    description.textContent =
      'Multiple users are editing the same data. How would you like to resolve this?'

    const buttonGroup = document.createElement('div')
    buttonGroup.className = 'space-y-2 mb-6'

    const strategyButtons = [
      {
        label: 'Let me choose which changes to keep',
        strategy: 'user_choice',
        className:
          'w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors',
      },
      {
        label: 'Keep the most recent changes',
        strategy: 'last_write_wins',
        className:
          'w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors',
      },
      {
        label: 'Try to merge changes automatically',
        strategy: 'merge',
        className:
          'w-full px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors',
      },
    ]

    for (const { label, strategy, className } of strategyButtons) {
      const button = document.createElement('button')
      button.className = className
      button.textContent = label
      button.addEventListener('click', () =>
        this.resolveConflict(conflictData.id, strategy)
      )
      buttonGroup.appendChild(button)
    }

    const dismissButton = document.createElement('button')
    dismissButton.className = 'w-full px-4 py-2 text-sm text-gray-500 hover:text-gray-700'
    dismissButton.textContent = 'Ignore for now'
    dismissButton.addEventListener('click', () => modal.remove())

    card.appendChild(header)
    card.appendChild(description)
    card.appendChild(buttonGroup)
    card.appendChild(dismissButton)
    modal.appendChild(card)

    document.body.appendChild(modal)

    setTimeout(() => {
      if (modal.parentNode) {
        modal.remove()
      }
    }, 30000)
  }

  /**
   * Resolve conflict using specified strategy
   */
  async resolveConflict(conflictId, strategy) {
    try {
      const conflict = this.conflictQueue.find(c => c.id === conflictId)
      if (!conflict) return
      
      const resolver = this.conflictStrategies[strategy]
      if (resolver) {
        await resolver(conflict)
        this.conflictQueue = this.conflictQueue.filter(c => c.id !== conflictId)
        this.emit('conflict_resolved', { conflictId, strategy })
      }
      
      // Remove conflict modal
      const modal = document.querySelector('.fixed')
      if (modal) modal.remove()
      
    } catch (error) {
      console.error('Error resolving conflict:', error)
      this.showNotification('Failed to resolve conflict', 'error')
    }
  }

  /**
   * Conflict resolution strategies
   */
  async resolveLastWriteWins(_conflict) {
    console.log('Resolved conflict using last write wins strategy')
  }

  async resolveUserChoice(conflict) {
    this.showConflictResolution(conflict)
  }

  async resolveMerge(conflict) {
    // Attempt automatic merge
    try {
      // This would implement smart merging logic
      console.log('Attempting automatic merge for conflict:', conflict)
      
      // For now, just show success
      this.showNotification('Changes merged successfully', 'success')
    } catch (error) {
      console.error('Merge failed:', error)
      this.showNotification('Automatic merge failed. Please resolve manually.', 'error')
    }
  }

  /**
   * Update active user information
   */
  updateActiveUser(childId, user, activityType) {
    if (!this.activeUsers.has(childId)) {
      this.activeUsers.set(childId, new Map())
    }
    
    const childUsers = this.activeUsers.get(childId)
    childUsers.set(user.id, {
      ...user,
      lastActivity: activityType,
      lastSeen: new Date(),
    })
    
    // Clean up inactive users (older than 5 minutes)
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000)
    for (const [userId, userData] of childUsers.entries()) {
      if (userData.lastSeen < fiveMinutesAgo) {
        childUsers.delete(userId)
      }
    }
  }

  /**
   * Get active users for a child
   */
  getActiveUsers(childId) {
    const childUsers = this.activeUsers.get(childId)
    return childUsers ? Array.from(childUsers.values()) : []
  }

  /**
   * Show activity indicator
   */
  showActivityIndicator(user, details) {
    // Create or update activity indicator
    let indicator = document.getElementById(`activity-${user.id}`)
    
    if (!indicator) {
      indicator = document.createElement('div')
      indicator.id = `activity-${user.id}`
      indicator.className = 'fixed top-20 right-4 bg-blue-100 border border-blue-200 rounded-lg p-2 text-sm text-blue-800 z-40'
      document.body.appendChild(indicator)
    }
    
    indicator.textContent = ''

    const row = document.createElement('div')
    row.className = 'flex items-center space-x-2'

    const pulse = document.createElement('div')
    pulse.className = 'w-2 h-2 bg-blue-500 rounded-full animate-pulse'

    const label = document.createElement('span')
    label.textContent = `${user.name} is ${details.action || 'active'}`

    row.appendChild(pulse)
    row.appendChild(label)
    indicator.appendChild(row)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (indicator.parentNode) {
        indicator.remove()
      }
    }, 5000)
  }

  /**
   * Show notification
   */
  showNotification(message, type = 'info', options = {}) {
    // Use existing notification system or create simple one
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date(),
      ...options
    }
    
    // Emit to notification system
    this.emit('notification', notification)
    
    // Also dispatch as custom event for other components
    window.dispatchEvent(new CustomEvent('realtimeNotification', {
      detail: notification
    }))
  }

  /**
   * Handle access revoked
   */
  handleAccessRevoked(childId) {
    // If currently viewing this child, redirect to dashboard
    const currentPath = window.location.pathname
    if (currentPath.includes(`/child/${childId}`) || currentPath.includes(`/children/${childId}`)) {
      window.location.href = '/dashboard'
    }
    
    // Clear any cached data for this child
    this.clearChildCache(childId)
  }

  /**
   * Clear cached data for a child
   */
  clearChildCache(childId) {
    // Clear from active users
    this.activeUsers.delete(childId)
    
    // Emit cache clear event
    this.emit('cache_clear', { childId })
  }

  /**
   * Navigate to invitation
   */
  navigateToInvitation(shareId) {
    // This would navigate to the invitation acceptance page
    window.location.href = `/invitations/${shareId}`
  }

  /**
   * Get current user email (would be from auth context)
   */
  getCurrentUserEmail() {
    // This would get the current user's email from auth context
    return window.currentUser?.email || ''
  }

  /**
   * Broadcast user activity
   */
  broadcastActivity(childId, activityType, details = {}) {
    if (this.websocketService && this.websocketService.isConnected) {
      this.websocketService.send({
        type: 'user_activity',
        child_id: childId,
        activity_type: activityType,
        details
      })
    }
  }

  /**
   * Start editing session
   */
  startEditing(childId, resourceType, resourceId) {
    this.broadcastActivity(childId, 'editing', {
      action: 'started editing',
      resource_type: resourceType,
      resource_id: resourceId
    })
  }

  /**
   * End editing session
   */
  endEditing(childId, resourceType, resourceId) {
    this.broadcastActivity(childId, 'editing', {
      action: 'stopped editing',
      resource_type: resourceType,
      resource_id: resourceId
    })
  }

  /**
   * Cleanup
   */
  cleanup() {
    this.eventListeners.clear()
    this.activeUsers.clear()
    this.conflictQueue = []
    
    if (this.websocketService) {
      // Remove event listeners
      window.removeEventListener('websocketEvent', this.handleWebSocketEvent)
    }
  }
}

// Create and export singleton instance
const realtimeSharingService = new RealtimeSharingService()

export default realtimeSharingService